import {CSSProperties, ElementType, ReactNode, useEffect, useId, useRef, useState} from 'react'
import {PlacesType, Tooltip as ReactTooltip} from 'react-tooltip'

// Same look as the tooltips on /allProducts/list (ProductSku, ProductPriceStatus):
// fixed positioning so it isn't clipped by the table's scroll container, wraps long
// text instead of growing across the screen, and sits above modals and dropdowns.
const TOOLTIP_STYLE: CSSProperties = {
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  zIndex: 9999,
}

interface TooltipProps {
  /** Text or markup shown in the tooltip. When empty, children render without a tooltip. */
  content: ReactNode
  children: ReactNode
  place?: PlacesType
  /** Max tooltip width in px. Defaults to 400. */
  maxWidth?: number
  /** Element that wraps the children and acts as the hover target. Defaults to 'span'. */
  as?: ElementType
  className?: string
  style?: CSSProperties
}

/**
 * Reusable hover tooltip (react-tooltip, like the rest of the app).
 * Wraps its children in a hover target and shows `content` on hover.
 *
 *   <Tooltip content='Edit'><button>…</button></Tooltip>
 */
const Tooltip = ({
  content,
  children,
  place = 'top',
  maxWidth = 400,
  as: Wrapper = 'span',
  className = '',
  style,
}: TooltipProps) => {
  const id = `ui-tooltip-${useId().replace(/:/g, '')}`
  const hasContent = content !== null && content !== undefined && content !== ''

  return (
    <>
      <Wrapper
        className={className}
        style={style}
        data-tooltip-id={hasContent ? id : undefined}
        data-tooltip-place={place}
      >
        {children}
      </Wrapper>
      {hasContent && (
        <ReactTooltip id={id} positionStrategy='fixed' style={{...TOOLTIP_STYLE, maxWidth}}>
          {content}
        </ReactTooltip>
      )}
    </>
  )
}

interface TruncatedTextProps {
  /** Full text. Shown in the tooltip when the rendered text is cut off. */
  text: string
  /** What to render in place of `text`, e.g. a Link. Defaults to `text`. */
  children?: ReactNode
  /** Number of lines before truncating. 1 = single line with an ellipsis. Defaults to 1. */
  lines?: number
  place?: PlacesType
  maxWidth?: number
  /** Element to render. Defaults to 'span' (rendered as a block). */
  as?: ElementType
  className?: string
  style?: CSSProperties
}

/**
 * Text that truncates with an ellipsis when it doesn't fit its column, and shows
 * the full value in a tooltip only when it's actually truncated. Re-checks on
 * resize, so it follows the table's responsive column widths — the same approach
 * as the Product / SKU description and status lines on /allProducts/list.
 */
const TruncatedText = ({
  text,
  children,
  lines = 1,
  place = 'top',
  maxWidth = 400,
  as: Element = 'span',
  className = '',
  style,
}: TruncatedTextProps) => {
  const ref = useRef<HTMLElement | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const id = `ui-truncated-${useId().replace(/:/g, '')}`

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () =>
      setIsTruncated(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight + 1)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, lines])

  const tooltipProps = {
    'data-tooltip-id': isTruncated ? id : undefined,
    'data-tooltip-content': isTruncated ? text : undefined,
    'data-tooltip-place': place,
  }

  // Single line: the ellipsis is its own element so it can sit a little apart from
  // the text (CSS `text-overflow` can't add spacing before the "…").
  const content =
    lines > 1 ? (
      <Element
        ref={ref}
        className={className}
        style={{
          minWidth: 0,
          maxWidth: '100%',
          display: '-webkit-box',
          WebkitLineClamp: lines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          wordBreak: 'break-word',
          ...style,
        }}
        {...tooltipProps}
      >
        {children ?? text}
      </Element>
    ) : (
      <Element
        className={className}
        style={{display: 'flex', minWidth: 0, maxWidth: '100%', whiteSpace: 'nowrap', ...style}}
        {...tooltipProps}
      >
        <span ref={ref} style={{minWidth: 0, overflow: 'hidden', textOverflow: 'clip'}}>
          {children ?? text}
        </span>
        {isTruncated && <span style={{flexShrink: 0, marginLeft: 4}}>…</span>}
      </Element>
    )

  return (
    <>
      {content}
      {isTruncated && (
        <ReactTooltip id={id} positionStrategy='fixed' style={{...TOOLTIP_STYLE, maxWidth}} />
      )}
    </>
  )
}

export {Tooltip, TruncatedText}
export type {TooltipProps, TruncatedTextProps}
